/**
 * スライドエディタを提供します。
 */
import { temporaryId } from './temporaryId.js';

const tinymce = window.tinymce;
const MT = window.MT;

export class SlideEditor {
    /**
     * SlideEditor クラスのインスタンスを生成します。
     *
     * @class SlideEditor
     *
     * @constructor
     * @param {string|HTMLElement} sourceField - SlideEditorを適用するソース要素またはそのID。
     * @param {Object} [options={}] - SlideEditorの設定オプション。
     * @param {string} [preset=''] - SlideEditorのプリセット設定文字列。（コンストラクタ内で現在は使用されていないが、将来の使用や拡張性のために保持されています。）
     * @param {Function|null} [onChange=null] - SlideEditorの内容が変更されたときに呼び出されるコールバック関数。
     */
    constructor(sourceField, options = {}, preset = '', onChange = null) {
        this.sourceElement = SlideEditor.getSourceElement(sourceField);
        if (!this.sourceElement) return;

        this.sourceElement.style.display = 'none';
        this.sourceElementId = SlideEditor.ensureElementHasId(this.sourceElement);

        const existingButton = document.getElementById(`${this.sourceElementId}-button`);
        if (existingButton) {
            this.buttonElement = existingButton;
        } else {
            this.buttonElement = SlideEditor.createButtonElement(this.sourceElementId);
            this.sourceElement.parentNode.insertBefore(this.buttonElement, this.sourceElement.nextElementSibling);
        }
        this.buttonElement.addEventListener('click', () => {
            this.open(this.sourceElementId, this.sourceElement.value);
        });

        const existingViewer = document.getElementById(`${this.sourceElementId}-view`);
        if (existingViewer) {
            this.viewerElement = existingViewer;
        } else {
            this.viewerElement = SlideEditor.createViewerElement(this.sourceElementId, this.sourceElement.value);
            this.sourceElement.parentNode.insertBefore(this.viewerElement, this.sourceElement.nextElementSibling);
        }

        if (preset !== '' && typeof SlideEditor.presets[preset] !== 'undefined') {
            this.config = { ...SlideEditor.presets[preset] };
        } else if (Object.keys(options).length > 0) {
            this.config = { ...options };
        }
        if (Object.keys(this.config).length > 0) {
            this.config.selector = `#${SlideEditor.editorId}`;
        }

        this.onChange = onChange;
        if (this.sourceElement) {
            this.sourceElement._slideEditorInstance = this;
        }
    }

    //--------------------------------------------------
    // 静的プロパティ
    //--------------------------------------------------

    /** @static @property {string} editorId - エディタのID */
    static editorId = 'mtapp-slide-editor';

    /** @static @property {HTMLElement|null} editor - エディタのDOM要素 */
    static editor = null;

    /** @static @property {HTMLElement|null} editorContainer - エディタのコンテナのDOM要素 */
    static editorContainer = null;

    /** @static @property {HTMLElement|null} editorCancelButton - エディタのキャンセルボタンのDOM要素 */
    static editorCancelButton = null;

    /** @static @property {HTMLElement|null} editorSaveButton - エディタの保存ボタンのDOM要素 */
    static editorSaveButton = null;

    /** @static @property {HTMLElement|null} editorFooter - エディタのフッターのDOM要素 */
    static editorFooter = null;

    /** @static @property {string} currentSourceElementId - 現在のソース要素のID */
    static currentSourceElementId = '';

    /** @static @property {HTMLElement|null} currentSourceElement - 現在のソース要素 */
    static currentSourceElement = null;

    /** @static @property {HTMLElement|null} currentViewerElement - 現在のビューア要素 */
    static currentViewerElement = null;

    /** @static @property {Object} backups - 設定のバックアップ */
    static backups = {};

    /**
     * @static
     * @property {Object} presets - エディタのプリセットの設定
     * @property {Object} presets.simple - シンプルなエディタの設定
     */
    static presets = {
        simple: {
            plugins: 'link lists',
            toolbar: 'bold link numlist bullist',
            menubar: false
        }
    };

    //--------------------------------------------------
    // インスタンスのプロパティ
    //--------------------------------------------------

    /** @property {string} sourceElementId - ソース要素のID */
    sourceElementId = '';

    /** @property {HTMLElement|null} sourceElement - ソース要素 */
    sourceElement = null;

    /** @property {HTMLElement|null} viewerElement - ビューア要素 */
    viewerElement = null;

    /** @property {HTMLElement|null} buttonElement - エディタを開くボタンの要素 */
    buttonElement = null;

    /** @property {Object} config - インスタンスの設定 */
    config = {};

    //--------------------------------------------------
    // 静的メソッド
    //--------------------------------------------------

    /**
     * エディタを初期化
     */
    static init() {
        if (typeof window.mtappVars.__system.slideEditorReady === 'boolean' && window.mtappVars.__system.slideEditorReady === true) {
            return;
        }

        let editor = document.getElementById(SlideEditor.editorId);
        if (!editor) {
            // HTML構造を作成
            const html = `
            <div id="mtapp-slide-editor-container">
                <textarea id="mtapp-slide-editor" class="form-control"></textarea>
                <div id="mtapp-slide-footer">
                    <button id="mtapp-slide-editor-save" type="button" class="btn btn-primary">${mtappVars.translate['Save Changes']}</button>
                    <button id="mtapp-slide-editor-cancel" type="button" class="btn btn-secondary ml-3">${mtappVars.translate['Cancel']}</button>
                </div>
            </div>
            `;

            // bodyタグの終了タグ直前にHTMLを挿入
            document.body.insertAdjacentHTML('beforeend', html.trim());
            editor = document.getElementById(SlideEditor.editorId);
        }

        // エディタ関係の要素を取得
        SlideEditor.editor = editor;
        SlideEditor.editorContainer = document.getElementById(`${SlideEditor.editorId}-container`);
        SlideEditor.editorCancelButton = document.getElementById(`${SlideEditor.editorId}-cancel`);
        SlideEditor.editorSaveButton = document.getElementById(`${SlideEditor.editorId}-save`);
        SlideEditor.editorFooter = document.getElementById('mtapp-slide-footer');

        // キャンセルボタンのイベントをセット
        SlideEditor.editorCancelButton.addEventListener('click', () => {
            SlideEditor.close();
        });

        // 保存ボタンのイベントをセット
        SlideEditor.editorSaveButton.addEventListener('click', () => {
            SlideEditor.save();
        });

        window.mtappVars.__system.SlideEditorReady = true;
    }

    /**
     * エディタのコンテンツを取得し Source Element に反映
     */
    static save() {
        SlideEditor.finishSourceMode();

        const editorContent = tinymce.get(SlideEditor.editorId).getContent();
        SlideEditor.currentSourceElement.value = editorContent;
        SlideEditor.currentSourceElement.dispatchEvent(new Event('input'));
        SlideEditor.currentSourceElement.dispatchEvent(new Event('blur'));
        SlideEditor.currentViewerElement.innerHTML = editorContent;

        // コールバック呼び出し
        if (
            SlideEditor.currentSourceElement &&
            SlideEditor.currentSourceElement._slideEditorInstance &&
            typeof SlideEditor.currentSourceElement._slideEditorInstance.onChange === 'function'
        ) {
            SlideEditor.currentSourceElement._slideEditorInstance.onChange(editorContent);
        }

        SlideEditor.close('save');
    }

    /**
     * エディタを閉じる
     *
     * @param type
     */
    static close(type = 'cancel') {
        if (type === 'cancel') {
            SlideEditor.finishSourceMode();
        }

        // TinyMCEを削除
        document.body.classList.remove('mtapp-has-slide-editor');
        SlideEditor.currentSourceElementId = '';
        SlideEditor.currentSourceElement = null;
        SlideEditor.editorContainer.classList.remove('active');
        SlideEditor.addStyle('mtapp-slide-editor-style', '');
        tinymce.get(SlideEditor.editorId).remove();
        MT.Editor.TinyMCE.config.external_plugins.mt_fullscreen = SlideEditor.backups.mt_fullscreen;
        delete SlideEditor.backups.mt_fullscreen;
    }

    /**
     * Source Element を取得
     *
     * @param {HTMLElement|String} target
     * @returns {HTMLElement|null}
     */
    static getSourceElement(target) {
        if (target instanceof HTMLElement) {
            return target;
        } else if (typeof target === 'string') {
            return document.getElementById(target);
        }
        return null;
    }

    /**
     * Source Element の ID を取得、なければ一時的な ID を付与
     *
     * @param element
     * @returns {string}
     */
    static ensureElementHasId(element) {
        let id = element.getAttribute('id');
        if (!id) {
            id = temporaryId();
            element.setAttribute('id', id);
        }
        return id;
    }

    /**
     * Viewer Element を作成
     *
     * @param id
     * @param value
     * @returns {HTMLDivElement}
     */
    static createViewerElement(id, value) {
        const viewer = document.createElement('div');
        viewer.setAttribute('id', `${id}-view`);
        viewer.classList.add('mtapp-slide-editor-viewer');
        viewer.innerHTML = value;
        return viewer;
    }

    /**
     * エディタを開くボタンを作成
     *
     * @param id
     * @returns {HTMLButtonElement}
     */
    static createButtonElement(id) {
        const button = document.createElement('button');
        button.setAttribute('id', `${id}-button`);
        button.setAttribute('type', 'button');
        button.classList.add('btn', 'btn-default', 'mt-3');
        button.innerText = '編集';
        return button;
    }

    /**
     * スタイルを追加
     *
     * @param id
     * @param cssContent
     */
    static addStyle(id, cssContent) {
        let style = document.getElementById(id);
        if (!style) {
            style = document.createElement('style');
            style.type = 'text/css';
            style.id = id;
            document.head.appendChild(style);
        }

        if (style.styleSheet) {
            // IEの場合
            style.styleSheet.cssText = cssContent;
        } else {
            // その他のブラウザの場合
            style.textContent = cssContent;
        }
    }

    /**
     * ソースモードを強制終了
     *
     * @static
     * @returns {void} 何も返しません
     */
    static finishSourceMode() {
        const labels = ['mt_source_mode', 'HTML編集モードの切り替え', 'Toggle HTML Edit Mode'];
        let button = null;
        for (const label of labels) {
            button = SlideEditor.editorContainer.querySelector(`#mtapp-slide-editor-container [aria-label="${label}"]`);
            if (button && button.getAttribute('aria-pressed') === 'true') {
                button.click();
                return;
            }
        }
    }

    //--------------------------------------------------
    // インスタンスのメソッド
    //--------------------------------------------------

    /**
     * エディタを開く
     */
    open() {
        SlideEditor.currentSourceElementId = this.sourceElementId;
        SlideEditor.currentSourceElement = this.sourceElement;
        SlideEditor.editor.value = this.sourceElement.value;
        SlideEditor.currentViewerElement = this.viewerElement;
        // active クラスを追加してスライドイン
        SlideEditor.editorContainer.classList.add('active');
        document.body.classList.add('mtapp-has-slide-editor');

        if (Object.keys(this.config).length > 0) {
            tinymce.init(this.config);
        } else {
            if (!(MT.Editor && MT.Editor.TinyMCE && MT.Editor.TinyMCE.config)) {
                const errorMessage = 'MT.Editor.TinyMCE.config is not defined.';
                throw new Error(errorMessage);
            }
            if (MT.Editor.TinyMCE.config.external_plugins && MT.Editor.TinyMCE.config.external_plugins.mt_fullscreen) {
                SlideEditor.backups.mt_fullscreen = MT.Editor.TinyMCE.config.external_plugins.mt_fullscreen;
                delete MT.Editor.TinyMCE.config.external_plugins.mt_fullscreen;
            }
            window.app.editors = window.app.editors || {};
            window.app.editors[SlideEditor.editorId] = new MT.EditorManager(SlideEditor.editorId, {
                format: 'richtext',
                wrap: true
            });
        }

        // エディタの高さ調整
        const footerHeight = SlideEditor.editorFooter.offsetHeight;
        window.setTimeout(() => {
            SlideEditor.addStyle(
                'mtapp-slide-editor-style',
                `
                    .tox-tinymce {
                        height: calc(100vh - ${footerHeight}px) !important;
                    }
                `
            );
        }, 300);
    }
}
